import {ASSISTANT_CHATBOT_NAME, CHATBOT_NAME, USER_NAME} from '../config/appConstants';
import {Citation, Message} from '../models/models';
import {ConversationDocumentInfo, ConversationMessageData} from "../models/conversation.models";

const sanitizeStreamChunk = (text: string): string => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;       // decoding HTML entities

    return textarea.value
        .replace(/\uFFFD/g, '') // replacement character
        .replace(/\uFEFF/g, '') // BOM
        .replace(/[\u0080-\u009F]/g, '') // control characters
        // .replace(/<[^>]*>/g, '') // HTML tags
        .normalize('NFC');
};

export const formatMessageForMarkdown = (text: string): string => {
    return text
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/\n\n+/g, '\n\n')
        .replace(/([^\n])\n([^\n])/g, '$1  \n$2');
};

export const extractCitations = (text: string, documents: ConversationDocumentInfo[]): { cleanText: string; citations: Citation[] } => {
    let cleanText = text;
    const allCitations: Citation[] = [];

    const citationRegex = /CITATIONS:\s*(\[[^\]]*(?:\][^\]]*)*\])/g;
    let match;

    while ((match = citationRegex.exec(text)) !== null) {
        try {
            const citations = JSON.parse(match[1]);

            if (Array.isArray(citations)) {
                allCitations.push(...citations.map(citation => (
                    {
                        content: sanitizeStreamChunk(citation.content),
                        documentId: citation.documentId,
                        documentName: documents.find(d => d.id === citation.documentId)?.name ?? '',
                    }
                )));
            }
        } catch (e) {
            console.error(e);
        }
    }

    cleanText = text.replace(/CITATIONS:\s*\[[^\]]*(?:\][^\]]*)*\]/g, '').trim();

    return { cleanText, citations: allCitations };
};

const THINKING_OPEN_MARKER = '<think>';
const THINKING_CLOSE_MARKER = '</think>';

const findPartialLength = (text: string, tag: string): number => {
    for (let i = tag.length - 1; i >= 1; i--) {
        if (text.endsWith(tag.slice(0, i))) return i;
    }
    return 0;
};

export const extractThinking = (text: string): { cleanText: string; thinkingText: string } => {
    const src = text ?? '';
    const openIndex = src.indexOf(THINKING_OPEN_MARKER);

    if (openIndex === -1) {
        const partialOpenTagLength = findPartialLength(src, THINKING_OPEN_MARKER);
        return {
            cleanText: partialOpenTagLength > 0 ? src.slice(0, -partialOpenTagLength) : src,
            thinkingText: ''
        };
    }

    const beforeThinkingText = src.slice(0, openIndex);

    const openedThinkingText = src.slice(openIndex + THINKING_OPEN_MARKER.length);

    const lastCloseIndex = openedThinkingText.lastIndexOf(THINKING_CLOSE_MARKER);

    if (lastCloseIndex === -1) {
        const partialCloseTagLength = findPartialLength(openedThinkingText, THINKING_CLOSE_MARKER);
        return {
            cleanText: beforeThinkingText,
            thinkingText: partialCloseTagLength > 0 ? openedThinkingText.slice(0, -partialCloseTagLength) : openedThinkingText,
        };
    }

    const thinkingRawText = openedThinkingText.slice(0, lastCloseIndex);
    const thinkingText = thinkingRawText
        .split(THINKING_CLOSE_MARKER)
        .map(part => part.trim())
        .filter(Boolean)
        .join('\n\n');

    const cleanText = beforeThinkingText + openedThinkingText.slice(lastCloseIndex + THINKING_CLOSE_MARKER.length);

    return { thinkingText, cleanText };
};

// TODO: AI-952 when view done, needs verification
export const mapServerMessageToClientMessage = (msg: ConversationMessageData, documents: ConversationDocumentInfo[]): Message => {
    const { thinkingText, cleanText: textWithoutThinking} = extractThinking(msg.content);
    const { cleanText, citations } = extractCitations(textWithoutThinking, documents);

    return {
        id: `msg-${msg.timestamp}-${Math.random()}`,
        author: msg.author === ASSISTANT_CHATBOT_NAME ? CHATBOT_NAME : msg.author,
        text: formatMessageForMarkdown(cleanText),
        thinking: thinkingText,
        isSystem: msg.author === ASSISTANT_CHATBOT_NAME,
        isLoading: false,
        documents: msg?.messageDocumentInfoResponses || undefined,
        timestamp: msg.timestamp,
        citations: citations.length > 0 ? citations : undefined,
    };
};

export const mapCollectionsToDataSourceIds = (
    conversationDataSources: any,
    conversationCollections: any,
): number[] => {
    const collections = (conversationDataSources ?? conversationCollections ?? []).map(
        (c: any) => ({ ...c, owner: { email: c?.owner?.email } }),
    );
    return collections.map((c: any) => c.id);
};

// TODO: AI-952 when view done, needs verification
export const createDefaultUserMessage = (
    text: string,
    attachedFiles: File[],
): Message => {
    return {
        id: `user-${Date.now()}-${Math.random()}`,
        author: USER_NAME,
        text,
        isSystem: false,
        documents:
            attachedFiles.length > 0
                ? attachedFiles.map(file => ({
                      documentId: -1,
                      documentName: file.name,
                  }))
                : undefined,
        timestamp: new Date().toISOString(),
        thinking: '',
    };
};

export const createDefaultLoadingMessage = (): Message => {
    return {
        id: `assistant-${Date.now()}-${Math.random()}`,
        author: ASSISTANT_CHATBOT_NAME,
        text: '',
        isSystem: true,
        isLoading: true,
        thinking: '',
    };
};

export const findLastUserMessage = (messages: Message[]) =>
    [...messages].reverse().find(m => !m.isSystem);

export const findLastAssistantMessage = (messages: Message[]) =>
    [...messages].reverse().find(m => m.isSystem);